import { lazy, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AppstoreOutlined, DollarOutlined, TeamOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";

import AtsRolesAndPermissionsSettings from "@av/apps/common-apps/platform/features/admin/settings/ats/RolesAndPermissionsSettings";
import HrRolesAndPermissionsSettings from "@av/apps/common-apps/platform/features/admin/settings/hr/RolesAndPermissionsSettings";
import { AppEndpoints, isDevelopment } from "@av/build-constants";
import { bffAxios } from "@av/config/api-config/axios-config";
import { platformQueryKeys } from "../platform-query-key-factory";

import type { PlatformAppEntry } from "./types";

const HrAppComponent = lazy(() => import("@av/apps/hr/HrApp"));
const PayrollAppComponent = lazy(() => import("@av/apps/payroll/PayrollApp"));
const RecruitmentAppComponent = lazy(() => import("@av/apps/recruitment/RecruitmentApp"));

export type UserProductsResponse = {
  products?: string[];
};

export function useApps() {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: platformQueryKeys.products(),
    queryFn: async (): Promise<string[]> => {
      const { data } = await bffAxios.get<UserProductsResponse>("/products");

      return data.products ?? [];
    },
  });
  const { data = [] } = query;
  const apps = useMemo<PlatformAppEntry[]>(
    () =>
      data
        .map((name) => {
          switch (name) {
            case "HR":
              return {
                name: t("root.app.hrCore"),
                productName: t("platform.adminSettings.products_hr"),
                path: "hr",
                apiUrl: AppEndpoints.hrApiUrl,
                icon: <TeamOutlined />,
                component: HrAppComponent,
                adminSettings: {
                  RolesAndPermissions: HrRolesAndPermissionsSettings,
                },
              };
            case "PAYROLL":
              return {
                name: t("root.app.payroll"),
                productName: t("platform.adminSettings.products_payroll"),
                path: "payroll",
                apiUrl: AppEndpoints.payrollApiUrl,
                icon: <DollarOutlined />,
                component: PayrollAppComponent,
              };
            case "ATS":
              return {
                name: t("root.app.recruitment"),
                productName: t("platform.adminSettings.products_ats"),
                path: "recruitment",
                apiUrl: AppEndpoints.atsApiUrl,
                icon: <AppstoreOutlined />,
                component: RecruitmentAppComponent,
                adminSettings: {
                  RolesAndPermissions: AtsRolesAndPermissionsSettings,
                },
              };

            default:
              if (isDevelopment) {
                throw Error("Invalid product: " + name);
              }
          }
        })
        .filter((i) => i !== undefined),
    [data, t]
  );

  return {
    ...query,
    data: apps,
  };
}
